import { useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Banknote,
  Scissors,
  ArrowLeft,
  Upload,
  Check,
  Wallet,
  Plus,
  RefreshCw,
  Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { AdminShell } from '@/components/admin-shell';
import { api, ApiCallError } from '@/lib/api';
import { useCurrentAdmin } from '@/hooks/use-current-admin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from '@/components/ui/empty';

const PANEL = 'rounded-lg border border-border bg-background shadow-sm';

// ── Tipos (espejan admin-cobros.ts / cobros-service.ts del backend) ──────────
interface TxnRow {
  id: string;
  tenantId: string;
  tenantName: string | null;
  chargedAmount: string;
  feeAmount: string;
  netAmount: string;
  feeMode: string;
  status: string;
  paidAt: string;
  settlementCutId: string | null;
}
interface CutRow {
  id: string;
  cutAt: string;
  status: string;
  txnCount: number;
  chargedTotal: string;
  feeTotal: string;
  netTotal: string;
  createdAt: string;
}
interface EntryRow {
  id: string;
  entryDate: string | null;
  amount: string;
  description: string | null;
  reference: string | null;
  matched: boolean;
}
interface PayoutRow {
  id: string;
  tenantId: string;
  tenantName: string | null;
  amount: string;
  feeTotal: string;
  txnCount: number;
  bankSnapshot: Record<string, unknown> | null;
  status: string;
  transferRef: string | null;
}
interface CutDetailResponse {
  cut: CutRow;
  transactions: TxnRow[];
  entries: EntryRow[];
  payouts: PayoutRow[];
}

// ── Formato ───────────────────────────────────────────────────────────────────
const nf = new Intl.NumberFormat('es-PY');
const gs = (v: string | number) => `Gs ${nf.format(Math.round(Number(v) || 0))}`;
const fmtDate = (iso: string | null) => (iso ? format(new Date(iso), 'dd/MM/yyyy HH:mm') : '—');
const fmtDay = (s: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : format(d, 'dd/MM/yyyy');
};
const errMsg = (e: unknown, fallback: string) =>
  e instanceof ApiCallError ? (e.body.message ?? e.body.error) : fallback;

// Etiquetas + variante de badge por estado.
const CUT_STATUS: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  open: { label: 'Abierto', variant: 'outline' },
  reconciling: { label: 'Conciliando', variant: 'secondary' },
  reconciled: { label: 'Conciliado', variant: 'default' },
  paid_out: { label: 'Liquidado', variant: 'default' },
};
const TXN_STATUS: Record<string, string> = {
  paid: 'Cobrado',
  cut: 'En corte',
  reconciled: 'Conciliado',
  settled: 'Liquidado',
};

function StatusBadge({ status }: { status: string }) {
  const meta = CUT_STATUS[status];
  return <Badge variant={meta?.variant ?? 'outline'}>{meta?.label ?? status}</Badge>;
}

export function AdminCobrosPage() {
  const { data: me } = useCurrentAdmin();
  const canMutate = me?.role === 'superadmin';

  return (
    <AdminShell>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-foreground">Cobros</h1>
        <p className="text-sm text-muted-foreground">
          Liquidación de pagos a los contactos: transacciones → corte → conciliación con el
          extracto → pago.
        </p>
      </div>

      <Tabs defaultValue="cortes">
        <TabsList className="inline-flex w-auto">
          <TabsTrigger value="cortes">Cortes</TabsTrigger>
          <TabsTrigger value="transacciones">Transacciones</TabsTrigger>
          <TabsTrigger value="ajustes">Ajustes</TabsTrigger>
        </TabsList>
        <TabsContent value="cortes" className="mt-4">
          <CortesTab canMutate={canMutate} />
        </TabsContent>
        <TabsContent value="transacciones" className="mt-4">
          <TransaccionesTab canMutate={canMutate} />
        </TabsContent>
        <TabsContent value="ajustes" className="mt-4">
          <AjustesTab canMutate={canMutate} />
        </TabsContent>
      </Tabs>
    </AdminShell>
  );
}

/* ─────────────────────────────── Cortes ─────────────────────────────── */

function CortesTab({ canMutate }: { canMutate: boolean }) {
  const [selectedCut, setSelectedCut] = useState<string | null>(null);
  if (selectedCut) {
    return <CutDetail cutId={selectedCut} canMutate={canMutate} onBack={() => setSelectedCut(null)} />;
  }
  return <CutsList canMutate={canMutate} onOpen={setSelectedCut} />;
}

function CutsList({ canMutate, onOpen }: { canMutate: boolean; onOpen: (id: string) => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['cobros-cuts'],
    queryFn: () => api<{ items: CutRow[] }>('/api/admin/cobros/cuts'),
  });

  const makeCut = useMutation({
    mutationFn: () => api<{ cut: CutRow; captured: number }>('/api/admin/cobros/cuts', { method: 'POST' }),
    onSuccess: (r) => {
      toast.success(
        r.captured > 0
          ? `Corte creado con ${r.captured} transacción(es).`
          : 'Corte creado (sin transacciones pendientes).'
      );
      qc.invalidateQueries({ queryKey: ['cobros-cuts'] });
      onOpen(r.cut.id);
    },
    onError: (e) => toast.error(errMsg(e, 'No se pudo crear el corte')),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Cada corte captura las transacciones cobradas pendientes y las prepara para conciliar y pagar.
        </p>
        {canMutate && (
          <Button disabled={makeCut.isPending} onClick={() => makeCut.mutate()}>
            <Scissors className="mr-2 size-4" /> Hacer corte
          </Button>
        )}
      </div>

      {isLoading || !data ? (
        <Skeleton className="h-64 w-full" />
      ) : data.items.length === 0 ? (
        <div className={PANEL}>
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Scissors />
              </EmptyMedia>
              <EmptyTitle>Sin cortes todavía</EmptyTitle>
              <EmptyDescription>
                Cuando haya pagos cobrados, hacé un corte para empezar la liquidación.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>
      ) : (
        <div className={`${PANEL} overflow-hidden`}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Transacciones</TableHead>
                <TableHead className="text-right">Cobrado</TableHead>
                <TableHead className="text-right">Comisión</TableHead>
                <TableHead className="text-right">A liquidar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((c) => (
                <TableRow key={c.id} className="cursor-pointer" onClick={() => onOpen(c.id)}>
                  <TableCell className="font-medium">{fmtDate(c.cutAt)}</TableCell>
                  <TableCell>
                    <StatusBadge status={c.status} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{c.txnCount}</TableCell>
                  <TableCell className="text-right tabular-nums">{gs(c.chargedTotal)}</TableCell>
                  <TableCell className="text-right tabular-nums">{gs(c.feeTotal)}</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">{gs(c.netTotal)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function CutDetail({
  cutId,
  canMutate,
  onBack,
}: {
  cutId: string;
  canMutate: boolean;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['cobros-cut', cutId],
    queryFn: () => api<CutDetailResponse>(`/api/admin/cobros/cuts/${cutId}`),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['cobros-cut', cutId] });
    qc.invalidateQueries({ queryKey: ['cobros-cuts'] });
  };

  const reconcile = useMutation({
    mutationFn: () =>
      api<{ matched: number; unmatchedEntries: number; unmatchedTxns: number }>(
        `/api/admin/cobros/cuts/${cutId}/reconcile`,
        { method: 'POST' }
      ),
    onSuccess: (r) => {
      toast.success(
        `Conciliado: ${r.matched} match(es). Sin conciliar: ${r.unmatchedTxns} txn, ${r.unmatchedEntries} extracto.`
      );
      invalidate();
    },
    onError: (e) => toast.error(errMsg(e, 'Error al conciliar')),
  });

  const buildPayouts = useMutation({
    mutationFn: () =>
      api<{ payoutCount: number }>(`/api/admin/cobros/cuts/${cutId}/build-payouts`, { method: 'POST' }),
    onSuccess: (r) => {
      toast.success(`Pagos armados para ${r.payoutCount} contacto(s).`);
      invalidate();
    },
    onError: (e) => toast.error(errMsg(e, 'Error al armar los pagos')),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-1 size-4" /> Volver
        </Button>
      </div>

      {isLoading || !data ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <>
          {/* Resumen del corte */}
          <div className={`${PANEL} p-4`}>
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="text-sm text-muted-foreground">Corte del {fmtDate(data.cut.cutAt)}</div>
                <StatusBadge status={data.cut.status} />
              </div>
              {canMutate && (
                <div className="flex gap-2">
                  {data.cut.status === 'reconciling' && (
                    <Button size="sm" disabled={reconcile.isPending} onClick={() => reconcile.mutate()}>
                      <RefreshCw className="mr-2 size-4" /> Conciliar
                    </Button>
                  )}
                  {data.cut.status === 'reconciled' && (
                    <Button size="sm" disabled={buildPayouts.isPending} onClick={() => buildPayouts.mutate()}>
                      <Wallet className="mr-2 size-4" /> Armar pagos
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Metric label="Transacciones" value={String(data.cut.txnCount)} />
              <Metric label="Cobrado" value={gs(data.cut.chargedTotal)} />
              <Metric label="Comisión" value={gs(data.cut.feeTotal)} />
              <Metric label="A liquidar" value={gs(data.cut.netTotal)} strong />
            </div>
          </div>

          {/* Extracto bancario */}
          <StatementSection
            cutId={cutId}
            entries={data.entries}
            status={data.cut.status}
            canMutate={canMutate}
            onImported={invalidate}
          />

          {/* Pagos a contribuyentes */}
          <PayoutsSection payouts={data.payouts} canMutate={canMutate} onPaid={invalidate} />
        </>
      )}
    </div>
  );
}

function Metric({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`tabular-nums ${strong ? 'text-lg font-bold text-foreground' : 'font-semibold'}`}>
        {value}
      </div>
    </div>
  );
}

/* ─────────────────────── Extracto bancario (import) ─────────────────────── */

function parseStatementCsv(text: string): {
  date: string | null;
  amount: number;
  description: string | null;
  reference: string | null;
}[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const rows: { date: string | null; amount: number; description: string | null; reference: string | null }[] = [];
  for (const line of lines) {
    const cols = line.split(/[;,\t]/).map((c) => c.trim());
    const amount = Number((cols[1] ?? '').replace(/[^\d]/g, ''));
    // Solo créditos (montos entrantes): la conciliación matchea contra el monto
    // cobrado, que siempre es positivo. Débitos y encabezados se descartan.
    if (!Number.isFinite(amount) || amount <= 0) continue;
    rows.push({
      date: (cols[0] || '').slice(0, 20) || null, // el backend cap-ea date a 20 chars
      amount,
      description: cols[2] || null,
      reference: cols[3] || null,
    });
  }
  return rows;
}

function StatementSection({
  cutId,
  entries,
  status,
  canMutate,
  onImported,
}: {
  cutId: string;
  entries: EntryRow[];
  status: string;
  canMutate: boolean;
  onImported: () => void;
}) {
  const [raw, setRaw] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const parsed = useMemo(() => parseStatementCsv(raw), [raw]);
  const canImport = status === 'open' || status === 'reconciling';

  const importMut = useMutation({
    mutationFn: () =>
      api<{ imported: number }>(`/api/admin/cobros/cuts/${cutId}/statement`, {
        method: 'POST',
        body: JSON.stringify({ rows: parsed }),
      }),
    onSuccess: (r) => {
      toast.success(`${r.imported} fila(s) importada(s).`);
      setRaw('');
      onImported();
    },
    onError: (e) => toast.error(errMsg(e, 'Error al importar el extracto')),
  });

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setRaw(text);
    if (fileRef.current) fileRef.current.value = '';
  }

  return (
    <div className={`${PANEL} space-y-3 p-4`}>
      <div className="font-semibold text-foreground">Extracto bancario</div>

      {canMutate && canImport && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Pegá las filas del extracto o subí un CSV. Formato por línea:{' '}
            <code className="font-mono">fecha, monto, descripción, referencia</code> (montos en guaraníes,
            sin decimales). El encabezado se ignora.
          </p>
          <Textarea
            rows={4}
            placeholder={'2026-05-20, 56000, Transferencia Bancard, REF123\n2026-05-20, 90000, ...'}
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
          />
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv,text/plain"
            className="hidden"
            onChange={onFile}
          />
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
              <Upload className="mr-2 size-4" /> Subir CSV
            </Button>
            <Button
              size="sm"
              disabled={parsed.length === 0 || importMut.isPending}
              onClick={() => importMut.mutate()}
            >
              Importar {parsed.length > 0 ? `(${parsed.length})` : ''}
            </Button>
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">Todavía no se importó el extracto de este corte.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead>
              <TableHead className="text-right">Monto</TableHead>
              <TableHead>Descripción</TableHead>
              <TableHead>Conciliado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map((e) => (
              <TableRow key={e.id}>
                <TableCell>{fmtDay(e.entryDate)}</TableCell>
                <TableCell className="text-right tabular-nums">{gs(e.amount)}</TableCell>
                <TableCell className="max-w-[260px] truncate text-muted-foreground">
                  {e.description ?? '—'}
                </TableCell>
                <TableCell>
                  {e.matched ? (
                    <Badge variant="default">
                      <Check className="mr-1 size-3" /> Sí
                    </Badge>
                  ) : (
                    <Badge variant="outline">No</Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

/* ─────────────────────── Pagos a contribuyentes ─────────────────────── */

function PayoutsSection({
  payouts,
  canMutate,
  onPaid,
}: {
  payouts: PayoutRow[];
  canMutate: boolean;
  onPaid: () => void;
}) {
  return (
    <div className={`${PANEL} space-y-3 p-4`}>
      <div className="font-semibold text-foreground">Pagos a contactos</div>
      {payouts.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Los pagos se arman cuando el corte está conciliado (botón “Armar pagos”).
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Contacto</TableHead>
              <TableHead>Cuenta</TableHead>
              <TableHead className="text-right">Txns</TableHead>
              <TableHead className="text-right">Comisión</TableHead>
              <TableHead className="text-right">A transferir</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acción</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payouts.map((p) => (
              <PayoutRowItem key={p.id} payout={p} canMutate={canMutate} onPaid={onPaid} />
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function PayoutRowItem({
  payout,
  canMutate,
  onPaid,
}: {
  payout: PayoutRow;
  canMutate: boolean;
  onPaid: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [ref, setRef] = useState('');
  const bank = payout.bankSnapshot as
    | { bankName?: string; accountNumber?: string; accountHolder?: string }
    | null;

  const payMut = useMutation({
    mutationFn: () =>
      api<{ payout: PayoutRow }>(`/api/admin/cobros/payouts/${payout.id}/pay`, {
        method: 'POST',
        body: JSON.stringify({ transferRef: ref || null }),
      }),
    onSuccess: () => {
      toast.success('Pago marcado como transferido.');
      setOpen(false);
      onPaid();
    },
    onError: (e) => toast.error(errMsg(e, 'Error al marcar el pago')),
  });

  return (
    <TableRow>
      <TableCell className="font-medium">{payout.tenantName ?? 'Sin nombre'}</TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {bank?.bankName ? (
          <span>
            {bank.bankName} · {bank.accountNumber}
          </span>
        ) : (
          <span className="text-amber-600 dark:text-amber-400">Sin cuenta cargada</span>
        )}
      </TableCell>
      <TableCell className="text-right tabular-nums">{payout.txnCount}</TableCell>
      <TableCell className="text-right tabular-nums">{gs(payout.feeTotal)}</TableCell>
      <TableCell className="text-right font-semibold tabular-nums">{gs(payout.amount)}</TableCell>
      <TableCell>
        {payout.status === 'paid' ? (
          <Badge variant="default">
            <Check className="mr-1 size-3" /> Pagado
          </Badge>
        ) : (
          <Badge variant="outline">Pendiente</Badge>
        )}
      </TableCell>
      <TableCell className="text-right">
        {payout.status === 'pending' && canMutate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline">
                Marcar pagado
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Marcar pago como transferido</DialogTitle>
                <DialogDescription>
                  {payout.tenantName ?? 'Contacto'} · {gs(payout.amount)}. Confirmá solo después de
                  hacer la transferencia real.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label htmlFor="transfer-ref">Referencia de la transferencia (opcional)</Label>
                <Input
                  id="transfer-ref"
                  placeholder="Ej: comprobante 0012345"
                  value={ref}
                  onChange={(e) => setRef(e.target.value)}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
                <Button disabled={payMut.isPending} onClick={() => payMut.mutate()}>
                  Confirmar pago
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </TableCell>
    </TableRow>
  );
}

/* ─────────────────────────────── Transacciones ─────────────────────────────── */

const TXN_FILTERS = ['all', 'paid', 'cut', 'reconciled', 'settled'] as const;

function TransaccionesTab({ canMutate }: { canMutate: boolean }) {
  const [status, setStatus] = useState<(typeof TXN_FILTERS)[number]>('all');
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['cobros-txns', status],
    queryFn: () =>
      api<{ items: TxnRow[] }>(
        `/api/admin/cobros/transactions${status === 'all' ? '' : `?status=${status}`}`
      ),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
          <SelectTrigger className="w-auto min-w-[170px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="paid">Cobradas</SelectItem>
            <SelectItem value="cut">En corte</SelectItem>
            <SelectItem value="reconciled">Conciliadas</SelectItem>
            <SelectItem value="settled">Liquidadas</SelectItem>
          </SelectContent>
        </Select>
        {canMutate && <RecordPaymentDialog onDone={() => qc.invalidateQueries({ queryKey: ['cobros-txns'] })} />}
      </div>

      {isLoading || !data ? (
        <Skeleton className="h-64 w-full" />
      ) : data.items.length === 0 ? (
        <div className={PANEL}>
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Banknote />
              </EmptyMedia>
              <EmptyTitle>Sin transacciones</EmptyTitle>
              <EmptyDescription>No hay pagos registrados con este filtro.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>
      ) : (
        <div className={`${PANEL} overflow-hidden`}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead className="text-right">Cobrado</TableHead>
                <TableHead className="text-right">Comisión</TableHead>
                <TableHead className="text-right">Neto</TableHead>
                <TableHead>Modo</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{fmtDate(t.paidAt)}</TableCell>
                  <TableCell className="font-medium">{t.tenantName ?? 'Sin nombre'}</TableCell>
                  <TableCell className="text-right tabular-nums">{gs(t.chargedAmount)}</TableCell>
                  <TableCell className="text-right tabular-nums">{gs(t.feeAmount)}</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">{gs(t.netAmount)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {t.feeMode === 'surcharge' ? 'Recargo' : 'Absorbe'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{TXN_STATUS[t.status] ?? t.status}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function RecordPaymentDialog({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [tenantId, setTenantId] = useState('');
  const [amount, setAmount] = useState('');

  const { data: tenants } = useQuery({
    queryKey: ['tenants-min'],
    queryFn: () =>
      api<{ items: { id: string; displayName: string; fullName: string }[] }>(
        '/api/admin/tenants?pageSize=100'
      ),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: () =>
      api('/api/admin/cobros/transactions', {
        method: 'POST',
        body: JSON.stringify({ tenantId, baseAmount: Number(amount.replace(/[^\d]/g, '')) }),
      }),
    onSuccess: () => {
      toast.success('Pago registrado.');
      setOpen(false);
      setTenantId('');
      setAmount('');
      onDone();
    },
    onError: (e) => toast.error(errMsg(e, 'Error al registrar el pago')),
  });

  const amountNum = Number(amount.replace(/[^\d]/g, ''));
  const valid = tenantId && amountNum >= 1;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 size-4" /> Registrar pago
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Registrar pago manual</DialogTitle>
          <DialogDescription>
            Registra un cobro confirmado a nombre de un contacto. La comisión se calcula según el
            modo de cada uno. (El flujo real entra por la pasarela; esto es para carga manual.)
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Contacto</Label>
            <Select value={tenantId} onValueChange={setTenantId}>
              <SelectTrigger>
                <SelectValue placeholder="Elegí un contacto" />
              </SelectTrigger>
              <SelectContent>
                {(tenants?.items ?? []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.displayName || t.fullName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="base-amount">Monto que cobra el contacto (Gs)</Label>
            <Input
              id="base-amount"
              inputMode="numeric"
              placeholder="Ej: 56000"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button disabled={!valid || create.isPending} onClick={() => create.mutate()}>
            Registrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────────── Ajustes ─────────────────────────────── */

function AjustesTab({ canMutate }: { canMutate: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['cobros-fee'],
    queryFn: () => api<{ amount: number }>('/api/admin/cobros/fee'),
  });
  const [fee, setFee] = useState<string>('');

  const save = useMutation({
    mutationFn: () =>
      api<{ amount: number }>('/api/admin/cobros/fee', {
        method: 'PUT',
        body: JSON.stringify({ amount: Number(fee.replace(/[^\d]/g, '')) }),
      }),
    onSuccess: () => {
      toast.success('Comisión actualizada.');
      setFee(''); // re-sincroniza el input con el valor canónico del server
      qc.invalidateQueries({ queryKey: ['cobros-fee'] });
    },
    onError: (e) => toast.error(errMsg(e, 'Error al guardar la comisión')),
  });

  if (isLoading || !data) return <Skeleton className="h-40 w-full" />;

  const current = fee === '' ? String(data.amount) : fee;

  return (
    <div className="max-w-md space-y-4">
      <div className={`${PANEL} space-y-3 p-4`}>
        <div className="flex items-center gap-2 font-semibold text-foreground">
          <Wallet className="size-4" /> Comisión por transacción
        </div>
        <p className="text-xs text-muted-foreground">
          Monto fijo (en guaraníes) que Actuo retiene por cada cobro. Según el modo de cada contacto,
          se le descuenta (absorbe) o se le recarga al cliente (recargo).
        </p>
        <div className="space-y-1">
          <Label htmlFor="fee">Comisión actual</Label>
          <Input
            id="fee"
            inputMode="numeric"
            disabled={!canMutate}
            value={current}
            onChange={(e) => setFee(e.target.value)}
          />
        </div>
        {canMutate && (
          <Button disabled={save.isPending} onClick={() => save.mutate()}>
            <Save className="mr-2 size-4" /> Guardar
          </Button>
        )}
      </div>
    </div>
  );
}
